import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Typography,
  Box,
  Divider,
  SelectChangeEvent,
} from '@mui/material';

import { useUpdateCanvas } from '../../data/canvas/useCanvases';
import { useDatastores } from '../../data/canvas/useDatastores';
import { Canvas, UpdateCanvasRequest } from '../../types/canvas.types';

interface CanvasConfigDialogProps {
  open: boolean;
  onClose: () => void;
  canvas: Canvas;
  accountId: string;
}

export const CanvasConfigDialog: React.FC<CanvasConfigDialogProps> = ({
  open,
  onClose,
  canvas,
  accountId,
}) => {
  const [name, setName] = useState(canvas.name || '');
  const [description, setDescription] = useState(
    (canvas.settings && canvas.settings['description']) || '',
  );
  const [datastoreId, setDatastoreId] = useState(canvas.datastore_id || '');

  const updateCanvasMutation = useUpdateCanvas(canvas.id, accountId);
  const { data: datastores, isLoading: datastoresLoading } =
    useDatastores(accountId);

  // Reset form when canvas changes
  useEffect(() => {
    setName(canvas.name || '');
    setDescription((canvas.settings && canvas.settings['description']) || '');
    setDatastoreId(canvas.datastore_id || '');
  }, [canvas]);

  const handleSave = async () => {
    const updateData: UpdateCanvasRequest = {
      name,
      settings: {
        ...canvas.settings,
        description,
      },
      datastore_id: datastoreId || undefined,
    };

    try {
      await updateCanvasMutation.mutateAsync(updateData);
      onClose();
    } catch (error) {
      console.error('Failed to update canvas:', error);
    }
  };

  const handleClose = () => {
    // Reset form to original values on cancel
    setName(canvas.name || '');
    setDescription((canvas.settings && canvas.settings['description']) || '');
    setDatastoreId(canvas.datastore_id || '');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Canvas Settings: {canvas.name}</DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Basic Configuration */}
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Canvas Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter canvas name"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (Optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter canvas description"
                multiline
                rows={3}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Datastore Configuration */}
          <Typography variant="h6" gutterBottom>
            Default Datastore
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Widgets on this canvas will use this datastore by default. Pages can
            override this setting.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth disabled={datastoresLoading}>
                <InputLabel>Default Datastore</InputLabel>
                <Select
                  value={datastoreId}
                  onChange={(e: SelectChangeEvent) =>
                    setDatastoreId(e.target.value)
                  }
                  label="Default Datastore"
                >
                  <MenuItem value="">
                    <em>None (pages must specify their own datastore)</em>
                  </MenuItem>
                  {datastores?.map((ds) => (
                    <MenuItem key={ds.id} value={ds.id}>
                      {ds.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {updateCanvasMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to update canvas. Please try again.
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={updateCanvasMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={updateCanvasMutation.isPending || !name.trim()}
        >
          {updateCanvasMutation.isPending ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
